import { Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";

const FREE_SHIPPING = 150;

/**
 * Slim site-wide announcement strip that lives at the very top of the fixed header.
 * Height ≈ 28px — Layout's main padding is bumped to compensate.
 *
 * Two modes:
 *  - Empty cart or subtotal already ≥ £150 → static marquee with generic shipping messages.
 *  - Cart has items and subtotal < £150 → dynamic "£X.XX away from free UK shipping" message
 *    that updates live as items are added / removed.
 */
export default function ShippingBanner() {
    const { t } = useTranslation();
    const { subtotal, count } = useCart();
    const remaining = Math.max(0, FREE_SHIPPING - subtotal);
    const isProgress = count > 0 && remaining > 0;
    const isEarned = count > 0 && remaining === 0;

    // Dynamic single-message mode (progress or earned) — no marquee animation.
    if (isProgress || isEarned) {
        const pct = Math.min(100, Math.round((subtotal / FREE_SHIPPING) * 100));
        return (
            <div
                className="w-full h-7 border-b border-[#00aeef]/20 bg-white overflow-hidden relative"
                data-testid="shipping-banner"
            >
                <div
                    className="absolute inset-y-0 left-0 bg-[#00aeef]/[0.10] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                />
                <div className="relative h-full flex items-center justify-center gap-2 px-4 text-[10px] uppercase tracking-[0.28em] text-[#0077b6] font-semibold text-center">
                    <Truck className="w-3 h-3 flex-shrink-0" />
                    {isEarned ? (
                        <span data-testid="shipping-earned">{t("shipBanner.earned")}</span>
                    ) : (
                        <span data-testid="shipping-progress">
                            {t("shipBanner.progress", { amount: remaining.toFixed(2) })}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // Default marquee mode.
    const messages = [
        t("shipBanner.free"),
        t("shipBanner.regions"),
        t("shipBanner.request"),
    ];
    // Duplicate the loop so the marquee is seamless.
    const loop = [...messages, ...messages];

    return (
        <div
            className="w-full h-7 border-b border-[#00aeef]/20 bg-white overflow-hidden relative"
            data-testid="shipping-banner"
        >
            <div className="h-full flex items-center nx-marquee whitespace-nowrap">
                {loop.map((m, i) => (
                    <span
                        key={`${m}-${i}`}
                        className="inline-flex items-center gap-2 px-8 text-[10px] uppercase tracking-[0.28em] text-[#0077b6] font-semibold"
                    >
                        <Truck className="w-3 h-3" />
                        {m}
                    </span>
                ))}
            </div>
        </div>
    );
}
